import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { colors } from '../../utils/colors';
import { GradientButton } from '../minicomponents/GradientButton';
import { IconRocketLaunchFilled, IconWarning } from '../minicomponents/icons';

interface ConfirmationPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  title: string;
  confirmButtonText: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  message?: string; // optional (for simple confirmations)
  disclaimer?: string;
  showRocketButton?: boolean;
}

const ConfirmationPopup: React.FC<ConfirmationPopupProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  message,
  disclaimer,
  confirmButtonText,
  cancelButtonText,
  confirmButtonColor = 'primary.main',
  showRocketButton = false,
}) => {
  const { t } = useTranslation();

  const handleConfirm = () => {
    onSubmit();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="tablet" fullWidth>
      <DialogTitle id="confirmation-popup-title">
        <Typography variant="h2">{title}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText id="confirmation-popup-description">
            {message && (
              <Typography sx={{ color: colors.text.primary }}>
                <Trans i18nKey={message} />
              </Typography>
            )}
            {disclaimer && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ color: colors.text.primary, mt: 1 }}
              >
                <IconWarning />
                <strong>{disclaimer}</strong>
              </Stack>
            )}
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pb: 2, pr: 2, gap: 1 }}>
        {showRocketButton ? (
          <GradientButton
            onSubmitButtonHandler={handleConfirm}
            buttonText={confirmButtonText}
            iconLeft={<IconRocketLaunchFilled />}
          />
        ) : (
          <Button
            onClick={handleConfirm}
            autoFocus={!disclaimer}
            variant="contained"
            sx={{
              backgroundColor: confirmButtonColor, // right now button color is ignored because of MuiButton-contained override in Theme.tsx
              minWidth: '100px',
            }}
          >
            {confirmButtonText}
          </Button>
        )}
        <Button onClick={onClose} sx={{ minWidth: '100px' }}>
          {cancelButtonText || t('app.buttons.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationPopup;
